import { format } from 'date-fns';
import { ru as russianLocale } from 'date-fns/locale/ru';
import React, { useCallback } from 'react';

import { Button, Space, Typography } from '@sravni/react-design-system';
import { PAGE_TYPE } from '@sravni/ugc/lib/organizations/constants';
import { getOrganizationHelpers } from '@sravni/ugc/lib/organizations/helpers';

import { EDIT, EDIT_MFO } from '../../../../constants/routes';
import { handleDownloadFiles } from '../../../../helpers/downloadFiles';
import { checkIfMfo, ReviewType } from '../../../../helpers/reviews';
import { IReviewItem } from '../../../../interfaces/reviews';
import { createLinkProps } from '../../../../utils/routing';
import { CustomTooltip } from '../../../UI/Tooltip';

import styles from './extra-info.module.scss';

type TProps = {
  review: IReviewItem;
  namesType: ReviewType['namesType'];
  isMobile: boolean;
  type: PAGE_TYPE;
  scrollToComments: () => void;
  isAuthor: boolean;
  handleEditClick: (type: 'full' | 'extraInfo') => () => void;
};

export const ExtraInfo: React.FC<TProps> = ({
  review,
  isMobile,
  type,
  scrollToComments,
  isAuthor,
  handleEditClick,
}) => {
  const isMfo = checkIfMfo(type);
  const attachments = review.attachments;
  const editRoute = isMfo ? EDIT_MFO : EDIT;
  const userName = review.reviewUserData?.userNameForAppeal;
  const userBirthDate = review.reviewUserData?.userBirthDateForAppeal;
  const userBirthDateFormatted = userBirthDate
    ? format(new Date(userBirthDate), 'd MMM yyyy', { locale: russianLocale })
    : null;
  const userPhoneFormatted = review.reviewUserData?.userPhoneForAppeal || review.authorPhoneFormatted;
  const message = review.messageForCompany;

  const handleDownloadFilesClick = useCallback(() => handleDownloadFiles(review.id)(attachments), [
    attachments,
    review.id,
  ]);

  const infoBlocksData = [
    {
      value: userName,
      label: 'Полное ФИО пользователя',
    },
    {
      value: userPhoneFormatted,
      label: 'Номер телефона',
    },
    {
      value: userBirthDateFormatted,
      label: 'Дата рождения',
    },
  ];

  const orgHelper = getOrganizationHelpers(review.reviewObjectType);

  const editLink = createLinkProps('/create', editRoute, {
    type: orgHelper.itemTypeUrl,
    organization: review.alias,
    id: review.id,
    queryParams: {
      scrollToExtraInfo: true,
    },
  });

  const tooltipContent = (
    <>
      <Typography.Text size={14} className={'h-mb-8'}>
        Эти данные считаются персональными и видны только вам.
      </Typography.Text>
      <Typography.Link
        href="https://www.sravni.ru/promo/soglasie-na-obrabotku-personalnyh-dannyh/"
        target="_blank"
        rel="noreferrer"
      >
        Подробнее о&nbsp;согласии на&nbsp;обработку персональных данных
      </Typography.Link>
    </>
  );

  const infoBlock = (label: string, text: string) => {
    return (
      <Space direction={'vertical'}>
        <Typography.Text size={12} className={'h-color-D60 h-mb-2'}>
          {label}
        </Typography.Text>
        <Typography.Text size={16} strong>
          {text}
        </Typography.Text>
      </Space>
    );
  };

  const button =
    review.canEditReviewHiddenFields && isAuthor ? (
      <a href={editLink.as}>
        <Button variant={'outlined'} size={36} block={isMobile} onClick={handleEditClick('extraInfo')}>
          Дополнить
        </Button>
      </a>
    ) : (
      <Button variant={'outlined'} size={36} block={isMobile} onClick={scrollToComments}>
        Ответить
      </Button>
    );

  return (
    <div className={styles.wrapper}>
      <Space size={8}>
        <Typography.Text size={16} strong className={isMobile ? 'h-mb-12' : 'h-mb-16'}>
          Персональная информация
        </Typography.Text>
        <CustomTooltip children={tooltipContent} />
      </Space>
      <Space direction={isMobile ? 'vertical' : 'horizontal'} align={'start'} size={isMobile ? 12 : 24}>
        {infoBlocksData.map(({ label, value }) => value && infoBlock(label, value))}

        {!isMobile && button}
      </Space>
      {message && (
        <Space className={isMobile ? 'h-mt-12' : 'h-mt-16'}>
          <Typography.Text size={16} dangerouslySetInnerHTML={{ __html: message }} />
        </Space>
      )}
      {!!attachments?.length && (
        <Space direction={'vertical'} className={isMobile ? 'h-mt-12' : 'h-mt-16'}>
          <Typography.Link onClick={handleDownloadFilesClick}>Скачать приложенные файлы</Typography.Link>
        </Space>
      )}
      {isMobile && <div className={isMobile ? 'h-mt-12' : ''}>{button}</div>}
    </div>
  );
};
